import { useEffect, useRef } from "react";
import useLang from "../i18n/LanguageContext";
import useMediaQuery from "../hooks/useMediaQuery";
import { HOLD_EVENT } from "./Work";
import "./Critter.css";

// Fired by a Key whenever it is pressed; detail: { x, y } of the key's center
export const KEY_EVENT = "site:keypress";
// Fired by the contact form when a message has gone out
export const SENT_EVENT = "site:sent";

// Things on the page the critter can stand on (it stands on their top edge):
// cards, and lines of text, which are slippery
const CARDS = ".display, .slide.is-active .slide__shot, .slide.is-active .shot__spot, .skill-key, .xp__screen, .about__label, .contact";
const TEXT = ".hero__title, .section__title, .slide.is-active .slide__name, .about__lead";
const PERCHES = `${CARDS}, ${TEXT}`;

// What it does, besides talking, about a [data-critter] thing
const ACTIONS = {
  flikk: "photos",
  deskwren: "remote",
  tadoy: "icecream",
  talk: "dance",
  enter: "dance",
  "skill-2": "spin",
  "skill-5": "dance",
  label: "hop",
  status: "wave",
  top: "hop",
};

// How often each thing it does by itself comes up, relative to the others
const HABITS = {
  wander: 3,
  visit: 4,
  stroll: 4,
  musing: 3,
  trip: 2,
  hopscotch: 2,
  exercise: 1,
  hum: 1,
  sneeze: 1,
  stare: 1,
  clock: 1,
};

const GRAVITY = 2400; // px/s²
const WALK = 150; // px/s
const RUN = 330;
const DRIVE = 560;
const GLIDE = 115; // falling speed under the parachute
const SPAWN_MS = 2600; // appears once the keyboard has typed the name
const POINTER_FRESH_MS = 3500; // follows the pointer for this long after it moved
const CURIOSITY = 0.55; // chance that a new pointer movement interests it at all
const REST_MS = 650; // pointer still for this long = looking at something
const SLEEP_MS = 60000; // naps after this long without the visitor doing anything
const AWAY_EVERY_MS = 15000; // it disappears for a bit this often
const SLIP_PER_S = 0.25; // chance per second of walking on text to lose its footing
const NEAR = 240; // px: how close a click or the pointer has to be to matter
const REMARK_GAP_MS = 3500; // breathing room between two remarks
const REMARK_AGAIN_MS = 45000; // before it brings the same thing up again
const VISITS_KEY = "critter:visits";
const TOUR_KEY = "critter:tour"; // set once the visitor has answered the tour offer

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
const pick = (list) => list[Math.floor(Math.random() * list.length)];

/**
 * A small keycap creature that lives on the page and mostly minds its own
 * business: it strolls along cards and headings (and slips off letters),
 * visits things and comments on them, runs off screen and comes back, trips,
 * exercises, naps. It also notices the visitor: sometimes follows the pointer,
 * talks about what it rests on, can be picked up and thrown, hops along with
 * typing and celebrates a sent message.
 */
const Critter = ({ theme }) => {
  const { t } = useLang();
  const reducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const rootRef = useRef(null);
  const sayRef = useRef(() => {});
  const lines = useRef(t.critter);
  lines.current = t.critter;

  useEffect(() => {
    if (reducedMotion) return undefined;
    const root = rootRef.current;
    const body = root.querySelector(".critter__body");
    const bubble = root.parentNode.querySelector(".critter__bubble");
    const sayEl = bubble.querySelector(".critter__say");
    const actEls = bubble.querySelectorAll(".critter__act");
    const limbs = root.querySelector(".critter__limbs");
    const car = root.querySelector(".critter__car");
    const prop = root.querySelector(".critter__prop");
    const layer = root.parentNode;
    const pupils = root.querySelectorAll(".critter__pupil");
    const legs = root.querySelectorAll(".critter__leg");
    const arms = root.querySelectorAll(".critter__arm");
    const L = () => lines.current;

    // Feet position in viewport px. `ground` is "floor", a perch element, or
    // null while in the air; `landing` is where a jump is meant to end.
    const s = {
      x: 0, y: 0, vx: 0, vy: 0,
      ground: "floor", landing: null,
      avoid: null, // the perch it has just slipped off
      awake: false, asleep: false, held: false,
      offstage: false, // allowed to walk past the edges of the screen
      hiding: false, gone: false, // ducked behind the edge it stands on / out of sight
      sink: 0, sinkTo: 0, // px it has sunk below that edge
      dropped: false, awayAt: -Infinity,
      chute: false, driving: false, // parachute open / sitting in its car
      holding: null, // what is in its raised hand: "phone", "cone", or "hand" (nothing)
      touring: false, advance: false, // giving the guided tour / "next" was pressed
      choices: null, // what the buttons in the bubble do right now
      talkUntil: 0, bubbleX: 0, bubbleY: 0, bubbleOn: null,
      curious: true, // interested in the pointer right now
      facing: 1, phase: 0, squash: 0, spin: 0, tilt: 0,
      flop: 0, flopNow: 0, // lying on its face: -1/1 for the side, eased
      fell: false, thrown: false,
      goal: 0, thinkAt: 0, jumpedAt: 0,
      route: [], fast: false, // x positions to go through before it thinks again
      script: [], // steps of what it is in the middle of doing
      habitAt: Infinity, lastHabit: null,
      bounces: 0, // hops still to do in a dance
      staring: 0,
      lookX: 0, lookY: 0, blinkAt: 0,
    };
    const pointer = { x: 0, y: 0, seen: false, movedAt: -Infinity, rested: true };
    let grab = null; // a press that started on the critter: { x, y, at, moved }
    let activeAt = performance.now();
    let yawnedFor = 0;
    let pokes = [];
    let lastScroll = window.scrollY;
    let lastWidth = window.innerWidth;
    let hiddenAt = 0;
    let fastAt = -Infinity;
    let typedAt = -Infinity;
    let keyHopAt = 0;
    let remarkedAt = -Infinity;
    const saidAt = {};
    const turn = {};
    let slide = null;
    let frame = 0;
    let last = performance.now();
    let talkTimer = 0;
    let moodTimer = 0;
    let resizeTimer = 0;
    let showTimers = []; // the steps of a little show in progress (see `shows`)
    let dustAt = 0;
    let rolling = false;
    const said = new Set();

    // ---------- Where it can stand ----------

    const isText = (g) => g !== "floor" && g.matches(TEXT);
    // Small things inside a screenshot; it balances on them
    const isSpot = (g) => g !== "floor" && g.matches(".shot__spot");

    // The edge it stands on. For text that is the first line, a little below
    // the top of the line box, where the letters actually start.
    const box = (el) => {
      if (!el.matches(TEXT)) return el.getBoundingClientRect();
      const range = document.createRange();
      range.selectNodeContents(el);
      const line = range.getClientRects()[0];
      if (!line) return el.getBoundingClientRect();
      return { left: line.left, right: line.right, top: line.top + line.height * 0.2, width: line.width };
    };

    const floorY = () => window.innerHeight - 8;
    const top = (g) => (g === "floor" ? floorY() : box(g).top);
    const span = (g) => {
      if (g === "floor") return s.offstage ? [-140, window.innerWidth + 140] : [36, window.innerWidth - 36];
      const r = box(g);
      const inset = isText(g) ? 12 : isSpot(g) ? Math.min(24, r.width * 0.3) : 24;
      return [r.left + inset, r.right - inset];
    };
    // A perch counts only while its top edge is comfortably on screen
    const usable = (el) => {
      if (!el.isConnected || !el.matches(PERCHES)) return false; // e.g. a slide no longer showing
      const r = box(el);
      return (
        r.width > (isSpot(el) ? 8 : 70) &&
        r.right > 60 &&
        r.left < window.innerWidth - 60 &&
        r.top > 110 &&
        r.top < window.innerHeight - 40
      );
    };
    const perches = () => Array.from(document.querySelectorAll(PERCHES)).filter(usable);

    // The surface at a point: the card it is inside, else the first perch
    // below it, else the floor
    const groundUnder = (p) => {
      let inside = null;
      let below = null;
      let belowTop = Infinity;
      perches().forEach((el) => {
        const r = el.getBoundingClientRect();
        if (p.x < r.left - 10 || p.x > r.right + 10) return;
        if (p.y >= r.top && p.y <= r.bottom) inside = el;
        else if (r.top > p.y && r.top < belowTop) {
          below = el;
          belowTop = r.top;
        }
      });
      return inside || below || "floor";
    };

    // ---------- Talking and expressions ----------

    // The bubble appears above where the critter is now and stays there, so
    // it can be read even if the critter then hops, falls or runs
    // `choices` adds buttons under the text: [[label, onClick], [label, onClick]]
    const say = (text, ms = 2600, choices = null) => {
      const now = performance.now();
      sayEl.textContent = text;
      s.choices = choices;
      bubble.classList.toggle("has-actions", !!choices);
      if (choices) actEls.forEach((el, i) => { el.textContent = choices[i][0]; });
      bubble.classList.add("is-on");
      s.bubbleX = clamp(s.x, 150, Math.max(150, window.innerWidth - 150));
      s.bubbleY = s.y;
      s.bubbleOn = s.ground;
      s.talkUntil = now + ms;
      clearTimeout(talkTimer);
      talkTimer = setTimeout(() => bubble.classList.remove("is-on"), ms);
      remarkedAt = now;
    };
    const hush = () => {
      clearTimeout(talkTimer);
      bubble.classList.remove("is-on");
      s.talkUntil = 0;
      s.choices = null;
    };
    const onChoice = (e) => {
      const index = Array.prototype.indexOf.call(actEls, e.target);
      if (index >= 0 && s.choices) s.choices[index][1]();
    };
    sayRef.current = (text) => s.awake && !s.asleep && say(text);

    // A passing expression: "is-happy" (eyes squeezed shut, grinning) or "is-waving"
    const mood = (name, ms) => {
      root.classList.remove("is-happy", "is-waving");
      root.classList.add(name);
      clearTimeout(moodTimer);
      moodTimer = setTimeout(() => root.classList.remove("is-happy", "is-waving"), ms);
    };

    // A puff of dust left on the ground where the car's back wheel is. It
    // stays where it was kicked up, so a moving car leaves a trail.
    const dust = (big) => {
      if (layer.querySelectorAll(".critter__dust").length > 40) return;
      const puff = document.createElement("span");
      const size = (big ? 20 : 12) + Math.random() * 16;
      puff.className = "critter__dust";
      puff.style.width = `${size}px`;
      puff.style.height = `${size}px`;
      puff.style.setProperty("--x", `${s.x - s.facing * (50 + Math.random() * 16) - size / 2}px`);
      puff.style.setProperty("--y", `${s.y - size * 0.7 - Math.random() * 8}px`);
      puff.style.setProperty("--dx", `${-s.facing * (14 + Math.random() * 30)}px`);
      puff.style.setProperty("--dy", `${-(10 + Math.random() * 26)}px`);
      puff.addEventListener("animationend", () => puff.remove());
      layer.appendChild(puff);
    };

    // ---------- Moving ----------

    // Hop on the spot and come back down on the same ground
    const hop = (speed) => {
      if (!s.ground) return;
      s.landing = s.ground;
      s.ground = null;
      s.vy = -speed;
      s.vx = 0;
    };

    // Leap in an arc that ends on `g` at x = `lx`
    const jumpTo = (g, lx) => {
      const rise = s.y - top(g); // > 0 when the target is higher up
      const height = rise > 0 ? rise + 70 : 60;
      const up = Math.sqrt((2 * height) / GRAVITY);
      const down = Math.sqrt((2 * (height - rise)) / GRAVITY);
      s.vy = -GRAVITY * up;
      s.vx = (lx - s.x) / (up + down);
      s.facing = s.vx >= 0 ? 1 : -1;
      s.landing = g;
      s.ground = null;
      s.jumpedAt = performance.now();
    };

    // Steps a script is made of. Each returns true once it is done; they are
    // only run while the critter is standing on something.
    const wait = (ms) => {
      let until = 0;
      return (now) => {
        if (!until) until = now + ms;
        return now >= until;
      };
    };
    const run = (fn) => () => {
      fn();
      return true;
    };
    const walkTo = (to, fast = false) => {
      let begun = false;
      return () => {
        if (!begun) {
          begun = true;
          s.fast = fast;
          s.route = [typeof to === "function" ? to() : to];
        }
        return !s.route.length;
      };
    };
    const leap = (choose) => {
      let begun = false;
      return () => {
        if (begun) return true; // back on something
        begun = true;
        const g = choose();
        if (!g || g === s.ground || (g !== "floor" && !usable(g))) return true;
        const [lo, hi] = span(g);
        jumpTo(g, lo + Math.random() * (hi - lo));
        return false;
      };
    };

    const stop = () => {
      holdSlide(false);
      s.script = [];
      s.route = [];
      s.bounces = 0;
      s.offstage = false;
      s.hiding = false;
      s.gone = false;
      s.dropped = false;
      s.holding = null;
      s.chute = false;
      s.driving = false;
      s.sinkTo = 0;
      s.flop = 0;
    };

    const land = (g) => {
      s.ground = g;
      s.landing = null;
      s.avoid = null;
      s.vx = 0;
      s.vy = 0;
      s.y = top(g);
      s.goal = s.x;
      s.squash = s.chute ? 0.4 : 1;
      s.chute = false;
      s.thinkAt = performance.now() + 250;
      if (s.dropped) {
        s.dropped = false; // the script that dropped it in carries on
      } else if (s.fell) {
        // Face down for a moment, then up again as if nothing happened
        s.fell = false;
        s.flop = s.facing;
        s.script = [
          wait(1300),
          run(() => {
            s.flop = 0;
            hop(240);
            say(pick(L().ouch), 3200);
          }),
        ];
      } else if (s.thrown) {
        s.thrown = false;
        s.script = [wait(350), run(() => say(pick(L().landed), 2400))];
      }
    };

    // Missed a letter: straight down, tumbling
    const slip = () => {
      s.avoid = s.ground;
      stop();
      s.ground = null;
      s.landing = null;
      s.vy = 40;
      s.vx = s.facing * 50;
      s.spin = s.facing * 220;
      s.fell = true;
      say(pick(L().slip), 1500);
    };

    // A script step: jump onto `selector` once it is in place (the page may
    // still be scrolling, or a slide still changing); after a while, carry on anyway
    const hopOnto = (selector) => {
      let begun = 0;
      let jumped = false;
      return (now) => {
        if (jumped) return true; // landed
        if (!begun) begun = now;
        const el = document.querySelector(selector);
        if (el && el === s.ground) return true;
        if (el && usable(el)) {
          const [lo, hi] = span(el);
          jumped = true;
          jumpTo(el, lo + (0.3 + Math.random() * 0.4) * (hi - lo));
          return false;
        }
        return now - begun > 1800;
      };
    };

    // ---------- Little shows about each project ----------
    // These run on timers next to whatever it is saying, instead of as script
    // steps, so they can play while a speech bubble is up.

    const endShow = () => {
      showTimers.forEach(clearTimeout);
      showTimers = [];
      s.holding = null;
    };
    const startShow = (steps) => {
      endShow();
      showTimers = steps.map(([ms, fn]) => setTimeout(() => s.awake && !s.held && fn(), ms));
    };

    // Something that appears on the page for a moment and removes itself
    const emit = (className, html, vars) => {
      const el = document.createElement("span");
      el.className = className;
      el.innerHTML = html;
      Object.keys(vars).forEach((name) => el.style.setProperty(name, vars[name]));
      el.addEventListener("animationend", (e) => e.target === el && el.remove());
      layer.appendChild(el);
    };

    // Flikk: fling a photo to one side. Right is "keep", left is "delete".
    const fling = (keep) => {
      const dir = keep ? 1 : -1;
      s.tilt = dir * 12;
      emit(`critter__photo critter__photo--${keep ? "keep" : "toss"}`, keep ? "✓" : "✕", {
        "--x": `${s.x + s.facing * 34 - 15}px`, // from its raised hand
        "--y": `${s.y - 80}px`,
        "--dx": `${dir * (110 + Math.random() * 40)}px`,
        "--turn": `${dir * (14 + Math.random() * 14)}deg`,
        "--hue": `${Math.floor(Math.random() * 360)}`,
      });
    };

    // Deskwren: the Mac's volume overlay, on the screenshot if it is on screen
    const volumeHud = () => {
      const shot = document.querySelector(".slide.is-active .slide__shot");
      const r = shot && shot.getBoundingClientRect();
      const onShot = r && r.width > 120 && r.top < window.innerHeight - 140 && r.bottom > 160;
      const x = onShot ? r.left + r.width / 2 : clamp(s.x + s.facing * 110, 70, window.innerWidth - 70);
      const y = onShot ? clamp(r.top + r.height * 0.36, 150, window.innerHeight - 80) : s.y - 60;
      emit(
        "critter__hud",
        '<svg viewBox="0 0 24 24" width="34" height="34"><path d="M4 9h4l5-4v14l-5-4H4z" fill="currentColor"/>' +
          '<path d="M16 8.5a5 5 0 0 1 0 7M18.5 6a8.5 8.5 0 0 1 0 12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>' +
          '<span class="critter__hud-bar"><i></i></span>',
        { "--x": `${x}px`, "--y": `${y}px` }
      );
    };

    // While it plays in a screenshot the slider must not move on to the next project
    let holding = false;
    const holdSlide = (hold) => {
      if (hold === holding) return;
      holding = hold;
      window.dispatchEvent(new CustomEvent(HOLD_EVENT, { detail: { hold } }));
    };

    // Flikk: it goes into the screenshot, stands on the phone, and presses the
    // app's three labels one after another: keep, delete, decide later
    const spotIn = (id) => `.slide.is-active .shot__spot[data-spot="${id}"]`;
    const pressSpot = (id, glyph, color, nudge, then) =>
      run(() => {
        const el = document.querySelector(spotIn(id));
        if (!el || s.ground !== el) return; // it never got there (the page moved on)
        const r = el.getBoundingClientRect();
        s.squash = 0.9;
        emit("critter__ping", "", { "--x": `${r.left + r.width / 2}px`, "--y": `${r.top + 12}px`, "--c": color });
        emit("critter__mark", glyph, { "--x": `${r.left + r.width / 2}px`, "--y": `${r.top - 70}px`, "--c": color });
        const frame = el.parentNode;
        frame.dataset.nudge = nudge;
        setTimeout(() => delete frame.dataset.nudge, 420);
        if (then) then();
      });
    const playFlikk = () => [
      run(() => holdSlide(true)),
      hopOnto(spotIn("phone")),
      wait(350),
      walkTo(() => span(s.ground)[1]),
      hopOnto(spotIn("keep")),
      wait(260),
      pressSpot("keep", "✓", "#58c26d", "right"),
      wait(850),
      hopOnto(spotIn("toss")),
      wait(260),
      pressSpot("toss", "✕", "#ef5a4c", "left"),
      wait(850),
      hopOnto(spotIn("later")),
      wait(260),
      pressSpot("later", "?", "#f0b24a", "up", () => say(pick(L().later), 2400)),
      wait(2400),
      run(() => holdSlide(false)),
    ];
    // Deskwren: press "Download", then try to stomp the closed laptop open;
    // when that fails, out comes the phone and the Mac's volume goes up
    const nudgeFrame = (id, way) =>
      run(() => {
        const el = document.querySelector(spotIn(id));
        if (!el || s.ground !== el) return;
        const frame = el.parentNode;
        frame.dataset.nudge = way;
        setTimeout(() => delete frame.dataset.nudge, 300);
      });
    const stomp = (id) => [run(() => hop(270)), nudgeFrame(id, "down"), wait(160)];
    const playDeskwren = () => [
      run(() => holdSlide(true)),
      hopOnto(spotIn("download")),
      wait(260),
      pressSpot("download", "↓", "#e0a85a", "down"),
      wait(850),
      hopOnto(spotIn("lid")),
      wait(300),
      walkTo(() => (span(s.ground)[0] + span(s.ground)[1]) / 2),
      ...stomp("lid"),
      ...stomp("lid"),
      ...stomp("lid"),
      run(() => {
        const lid = document.querySelector(spotIn("lid"));
        if (!lid || s.ground !== lid) return;
        say(pick(L().lid), 3000);
        shows.remote();
      }),
      wait(3300),
      run(() => holdSlide(false)),
    ];

    // A screenshot can be played in only while its first spot is on screen
    const canPlay = (firstSpot) => {
      const el = document.querySelector(spotIn(firstSpot));
      return !!el && usable(el);
    };

    const shows = {
      photos: () =>
        startShow([
          [0, () => { s.holding = "hand"; }],
          [350, () => fling(true)],
          [1000, () => fling(false)],
          [1650, () => fling(true)],
          [2300, () => fling(false)],
          [2900, endShow],
        ]),
      remote: () =>
        startShow([
          [0, () => { s.holding = "phone"; }],
          [500, () => { volumeHud(); s.squash = 0.5; }],
          [3300, endShow],
        ]),
      icecream: () =>
        startShow([
          [0, () => { s.holding = "cone"; }],
          [700, () => mood("is-happy", 900)],
          [2000, () => mood("is-happy", 900)],
          [3400, endShow],
        ]),
    };

    const act = (name) => {
      if (name === "photos" && canPlay("phone")) s.script.push(...playFlikk());
      else if (name === "remote" && canPlay("download")) s.script.push(...playDeskwren());
      else if (shows[name]) shows[name]();
      else if (name === "hop") hop(380);
      else if (name === "happy") mood("is-happy", 1400);
      else if (name === "wave") mood("is-waving", 1800);
      else if (name === "spin") {
        s.spin = 360;
        hop(540);
      } else if (name === "dance") {
        s.bounces = 3;
        hop(300);
      } else if (name === "swipe" && s.ground) {
        // Flikk's gesture: dash right, then left, then back
        const [min, max] = span(s.ground);
        s.fast = true;
        s.route = [s.x + 80, s.x - 80, s.x].map((x) => clamp(x, min, max));
      } else if (name === "party") {
        mood("is-happy", 2600);
        s.spin = 360;
        s.bounces = 4;
        hop(620);
      }
    };

    // Say the next line about `key` (a data-critter name), unless it has just
    // talked or brought that up recently
    const remark = (key, { force = false, again = REMARK_AGAIN_MS } = {}) => {
      const options = L().about[key];
      if (!options || !s.awake || s.asleep || s.held) return;
      const now = performance.now();
      if (!force && (now - remarkedAt < REMARK_GAP_MS || now - (saidAt[key] || -Infinity) < again)) return;
      turn[key] = (turn[key] || 0) + 1;
      saidAt[key] = now;
      say(options[(turn[key] - 1) % options.length], 3000);
      act(ACTIONS[key]);
    };

    const wake = () => {
      if (!s.asleep) return;
      s.asleep = false;
      root.classList.remove("is-asleep");
      hop(380);
      say(pick(L().wake), 2400);
      s.habitAt = performance.now() + 6000;
    };

    const poke = () => {
      const now = performance.now();
      stop();
      pokes = pokes.filter((at) => now - at < 1500).concat(now);
      if (pokes.length >= 3) {
        pokes = [];
        s.spin = 360;
        hop(640);
        say(L().dizzy);
      } else if (pokes.length === 2) {
        hop(420);
        mood("is-happy", 1100);
        offerTour(); // clicked twice: that is how to ask for the tour
        return;
      } else {
        hop(500);
        say(pick(L().poke), 1600);
      }
      mood("is-happy", 1100);
      s.habitAt = now + 6000;
    };

    // ---------- The guided tour ----------

    const remember = (key) => {
      try {
        localStorage.setItem(key, "1");
      } catch (error) {
        // storage unavailable: it will simply offer again next time
      }
    };

    const endTour = (line) => {
      s.touring = false;
      stop();
      if (document.activeElement && document.activeElement !== document.body) document.activeElement.blur();
      say(line, 3800);
      mood("is-waving", 2200);
      s.habitAt = performance.now() + 9000;
      s.awayAt = performance.now();
    };

    // Say one thing and wait: until "next" is pressed, or long enough to read it
    const speak = (text) => {
      let until = 0;
      return (now) => {
        if (!until) {
          until = now + 3000 + text.length * 65;
          s.advance = false;
          say(text, 600000, [
            [L().tour.next, () => { s.advance = true; }],
            [L().tour.stop, () => endTour(L().tour.stopped)],
          ]);
          return false;
        }
        if (!s.advance && now < until) return false;
        hush();
        return true;
      };
    };

    // Scroll the page so `selector` is at the top, and wait for it to settle
    const scrollTo = (selector) => {
      let begun = 0;
      let lastY = 0;
      let stillSince = 0;
      return (now) => {
        if (!begun) {
          begun = now;
          const el = selector === "#top" ? null : document.querySelector(selector);
          const y = el ? el.getBoundingClientRect().top + window.scrollY - 8 : 0;
          window.scrollTo({ top: Math.max(0, y), behavior: "smooth" });
        }
        if (window.scrollY !== lastY) {
          lastY = window.scrollY;
          stillSince = now;
        }
        return now - begun > 2600 || (now - begun > 400 && now - stillSince > 220);
      };
    };

    const tourSteps = () => {
      const T = L().tour;
      // Show something by pressing its key; focusing it also pauses its autoplay
      const press = (selector) =>
        run(() => {
          const el = document.querySelector(selector);
          if (!el) return;
          el.focus({ preventScroll: true });
          el.click();
        });
      const on = hopOnto;
      const shot = ".slide.is-active .slide__shot";
      const show = (id) => run(() => act(ACTIONS[id]));
      const project = (id, play) => [
        press(`.slider__tab[data-critter="${id}"]`),
        wait(900),
        on(shot),
        ...(play ? play() : [show(id)]),
        speak(T[id]),
      ];
      return [
        run(() => say(T.start, 1800)),
        wait(1800),
        scrollTo("#top"),
        on(".display"),
        speak(T.hero),
        speak(T.hero2),
        scrollTo("#work"),
        press('.slider__tab[data-critter="flikk"]'),
        wait(900),
        on(shot),
        speak(T.work),
        ...playFlikk(),
        speak(T.flikk),
        ...project("deskwren", playDeskwren),
        ...project("tadoy"),
        scrollTo("#skills"),
        on('[data-critter="skill-0"] .skill-key'),
        speak(T.skills),
        on('[data-critter="skill-2"] .skill-key'),
        speak(T.skills2),
        scrollTo("#experience"),
        press('[data-critter="xp-1"]'),
        wait(700),
        on(".xp__screen"),
        speak(T.xp1),
        press('[data-critter="xp-0"]'),
        wait(700),
        speak(T.xp0),
        scrollTo("#about"),
        on(".about__label"),
        speak(T.about),
        scrollTo("#contact"),
        on(".contact"),
        speak(T.contact),
        run(() => endTour(T.end)),
      ];
    };

    const startTour = () => {
      remember(TOUR_KEY);
      stop();
      s.touring = true;
      s.script = tourSteps();
    };

    function offerTour() {
      if (!s.awake || s.asleep || s.touring || s.held) return;
      stop();
      // Stay put while the question is up
      s.script = [wait(10000)];
      say(L().tour.offer, 10000, [
        [L().tour.yes, startTour],
        [
          L().tour.no,
          () => {
            remember(TOUR_KEY);
            stop();
            say(L().tour.later, 3800);
          },
        ],
      ]);
    }

    // ---------- A life of its own ----------

    // Each habit returns the steps to carry out, or null if it can't right now
    const habits = {
      wander: () => [walkTo(() => { const [lo, hi] = span(s.ground); return lo + Math.random() * (hi - lo); })],

      // Go and stand by something on the page and say what it thinks of it
      visit: () => {
        const h = window.innerHeight;
        const now = performance.now();
        const things = Array.from(document.querySelectorAll("[data-critter]")).filter((el) => {
          const r = el.getBoundingClientRect();
          const key = el.dataset.critter;
          return L().about[key] && r.width && r.top > 120 && r.bottom < h - 20 && now - (saidAt[key] || -Infinity) > REMARK_AGAIN_MS;
        });
        if (!things.length) return null;
        const thing = pick(things);
        const r = thing.getBoundingClientRect();
        const cx = r.left + r.width / 2;
        return [
          leap(() => groundUnder({ x: cx, y: r.top + r.height / 2 })),
          walkTo(() => { const [lo, hi] = span(s.ground); return clamp(cx + (Math.random() < 0.5 ? -44 : 44), lo, hi); }),
          run(() => remark(thing.dataset.critter, { force: true })),
        ];
      },

      // Walk along a heading, end to end (if it makes it)
      stroll: () => {
        const texts = perches().filter((g) => isText(g) && g !== s.ground);
        if (!texts.length) return null;
        const g = pick(texts);
        return [leap(() => g), walkTo(() => span(s.ground)[0]), walkTo(() => span(s.ground)[1]), wait(400)];
      },

      // Disappear for a few seconds and come back. It leaves by running off
      // the screen or ducking behind a card; it returns by running in,
      // dropping from above, or popping up from behind a card.
      runAway: () => {
        const cards = () => Array.from(document.querySelectorAll(CARDS)).filter(usable);
        const { innerWidth: w } = window;

        const exit = () => (s.x < w / 2 ? -130 : w + 130);
        const byCar = [
          leap(() => "floor"),
          run(() => {
            say(pick(L().carLeave), 1600);
            s.facing = s.x < w / 2 ? -1 : 1;
            s.driving = true;
            hop(190); // into the seat
          }),
          wait(1600),
          run(() => {
            s.offstage = true;
          }),
          walkTo(exit, true),
        ];
        const onFoot =
          cards().length && Math.random() < 0.5
            ? [
                leap(() => (s.ground !== "floor" && s.ground.matches(CARDS) ? s.ground : pick(cards()))),
                run(() => say(pick(L().hiding), 1500)),
                wait(1500),
                run(() => {
                  if (s.ground === "floor") return; // nothing to hide behind after all
                  s.hiding = true;
                  s.sinkTo = 96;
                }),
                wait(700),
                run(() => {
                  s.gone = s.hiding;
                }),
              ]
            : [
                leap(() => "floor"),
                run(() => {
                  say(pick(L().leaving), 1700);
                  s.offstage = true;
                }),
                wait(1700),
                // Out through whichever edge is closer
                walkTo(exit, true),
              ];
        const leave = Math.random() < 0.34 ? byCar : onFoot;

        // From the sky: like a stone, or under a parachute
        const fromAbove = (chute) => [
          run(() => {
            stop();
            s.ground = null;
            s.landing = null;
            s.sink = 0;
            s.x = 120 + Math.random() * (w - 240);
            s.y = chute ? -40 : -90;
            s.vx = 0;
            s.vy = 0;
            s.dropped = true;
            s.chute = chute;
            // stop() emptied the script; this is what happens after landing
            const line = chute ? L().chute : L().dropIn;
            s.script = [run(() => 0), wait(300), run(() => say(pick(line), 3200))];
          }),
        ];
        const dropIn = fromAbove(false);
        const glideIn = fromAbove(true);
        const driveIn = [
          run(() => {
            stop();
            s.offstage = true;
            s.driving = true;
            s.ground = "floor";
            s.x = Math.random() < 0.5 ? -130 : w + 130;
            s.y = floorY();
            // stop() emptied the script; carry on from here
            s.script = [
              run(() => 0),
              walkTo(() => (s.x < 0 ? 170 + Math.random() * 200 : w - 170 - Math.random() * 200), true),
              run(() => {
                s.offstage = false;
              }),
              wait(500),
              // Out of the car, which then disappears
              run(() => {
                s.driving = false;
                hop(330);
                say(pick(L().carBack), 4000);
              }),
            ];
          }),
        ];
        const runIn = [
          run(() => {
            s.gone = false;
            s.hiding = false;
            s.driving = false;
            s.sink = 0;
            s.sinkTo = 0;
            if (!s.offstage) {
              // It left by hiding: start from just outside an edge
              s.offstage = true;
              s.ground = "floor";
              s.x = Math.random() < 0.5 ? -110 : w + 110;
              s.y = floorY();
            } else if (Math.random() < 0.5) {
              s.x = w - s.x; // sometimes from the other side
            }
          }),
          walkTo(() => (s.x < 0 ? 150 + Math.random() * 160 : w - 150 - Math.random() * 160), true),
          run(() => {
            s.offstage = false;
            say(pick(L().back), 4000);
            mood("is-waving", 2000);
          }),
        ];
        const popUp = [
          run(() => {
            const card = pick(cards());
            if (!card) {
              s.script = [run(() => 0), ...dropIn];
              return;
            }
            const [lo, hi] = [box(card).left + 40, box(card).right - 40];
            s.offstage = false;
            s.driving = false;
            s.ground = card;
            s.x = lo + Math.random() * (hi - lo);
            s.y = top(card);
            s.goal = s.x;
            s.hiding = true;
            s.sink = 96;
            s.sinkTo = 96;
            s.gone = false;
          }),
          wait(400),
          // First just the eyes…
          run(() => {
            s.sinkTo = 44;
            say(pick(L().peek), 1500);
          }),
          wait(1500),
          run(() => {
            s.sinkTo = 0;
          }),
          wait(450),
          run(() => {
            s.hiding = false;
            say(pick(L().back), 4000);
            mood("is-waving", 2000);
          }),
        ];

        return [...leave, wait(3000 + Math.random() * 1500), ...pick([dropIn, glideIn, runIn, driveIn, popUp])];
      },

      musing: () => [run(() => say(pick(L().musings), 3600))],

      // Trips over nothing and lies there for a second
      trip: () => [
        walkTo(() => { const [lo, hi] = span(s.ground); return lo + Math.random() * (hi - lo); }),
        run(() => {
          s.flop = s.facing;
          say(pick(L().trip), 1500);
        }),
        wait(1700),
        run(() => {
          s.flop = 0;
          hop(260);
          say(pick(L().tripUp), 2000);
        }),
      ],

      // From skill key to skill key
      hopscotch: () => {
        const keys = Array.from(document.querySelectorAll(".skill-key")).filter(usable);
        if (keys.length < 3) return null;
        const order = s.x < window.innerWidth / 2 ? keys : keys.slice().reverse();
        return [run(() => say(L().hopscotch, 1800)), ...order.map((k) => leap(() => k))];
      },

      exercise: () => {
        const squat = [run(() => { s.squash = 1.4; }), wait(480)];
        return [run(() => say(L().exercise[0], 1500)), wait(800), ...squat, ...squat, ...squat, run(() => say(L().exercise[1], 2600))];
      },

      hum: () => [run(() => { say(L().hum, 2200); act("dance"); })],

      sneeze: () => [run(() => { say(L().sneeze, 1400); s.tilt = 24; hop(240); })],

      stare: () => [
        walkTo(() => { const [lo, hi] = span(s.ground); return clamp(window.innerWidth / 2, lo, hi); }),
        run(() => {
          s.staring = performance.now() + 2600;
          say(pick(L().stare), 2600);
        }),
        wait(2600),
      ],

      clock: () => [
        run(() => {
          const time = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
          say(L().clock.replace("{t}", time), 3000);
        }),
      ],
    };

    const startHabit = () => {
      // Disappearing runs on its own clock; everything else is picked at random
      const now = performance.now();
      if (now - s.awayAt > AWAY_EVERY_MS) {
        s.awayAt = now;
        s.lastHabit = "runAway";
        s.script = habits.runAway();
        return;
      }
      const names = Object.keys(HABITS).filter((name) => name !== s.lastHabit);
      const total = names.reduce((sum, name) => sum + HABITS[name], 0);
      let roll = Math.random() * total;
      const name = names.find((n) => (roll -= HABITS[n]) < 0) || "wander";
      s.lastHabit = name;
      s.script = habits[name]() || habits.wander();
    };

    const following = (now) => pointer.seen && s.curious && now - pointer.movedAt < POINTER_FRESH_MS;

    // Keep the pointer company: stand next to it, on whatever is under it
    const follow = (now) => {
      const [min, max] = span(s.ground);
      const side = s.x < pointer.x ? -1 : 1;
      // Change surface only once the pointer has settled somewhere
      if (now - pointer.movedAt > 320 && now - s.jumpedAt > 1300) {
        const under = groundUnder(pointer);
        if (under !== s.ground) {
          const [lo, hi] = span(under);
          jumpTo(under, clamp(pointer.x + side * 56, lo, hi));
          return;
        }
      }
      s.fast = false;
      s.goal = clamp(pointer.x + side * 64, min, max);
      s.thinkAt = now + 120;
    };

    const onGround = (now, dt) => {
      // Ride the perch as the page scrolls; let go when it leaves the screen
      if (s.ground !== "floor" && !usable(s.ground)) {
        if (s.touring) s.route = []; // the tour scrolls the page itself; it carries on after landing
        else stop();
        s.ground = null;
        s.landing = null;
        s.vy = 0;
        return;
      }
      s.y = top(s.ground);
      const [min, max] = span(s.ground);
      s.x = clamp(s.x, min, max);
      if (s.asleep) return;

      if (s.bounces > 0) {
        s.bounces -= 1;
        s.tilt = s.bounces % 2 ? 14 : -14;
        hop(300);
        return;
      }

      if (s.script.length) {
        if (s.script[0](now)) s.script.shift();
        if (!s.ground) return;
      } else if (!s.route.length) {
        if (now - activeAt > SLEEP_MS) {
          s.asleep = true;
          s.vx = 0;
          root.classList.add("is-asleep");
          hush();
          return;
        }
        if (yawnedFor !== activeAt && now - activeAt > SLEEP_MS - 7000) {
          yawnedFor = activeAt;
          say(L().yawn, 2200);
        }
        if (now < s.talkUntil) {
          // Finish the sentence before going anywhere
        } else if (following(now)) {
          if (now > s.thinkAt) follow(now);
          if (!s.ground) return;
        } else if (now > s.habitAt) {
          s.habitAt = now + 4000 + Math.random() * 6000;
          startHabit();
          return;
        }
      }

      // Stands still while lying down, hiding, or saying something (a line
      // said on the run stays behind instead)
      const talking = now < s.talkUntil && !(s.route.length && s.fast);
      if (s.flop || s.hiding || talking) {
        s.vx = 0;
        return;
      }
      if (s.route.length) {
        s.goal = s.route[0];
        if (Math.abs(s.goal - s.x) <= 6) s.route.shift();
      }
      const gap = clamp(s.goal, min, max) - s.x;
      if (Math.abs(gap) > 5) {
        const onFoot = (s.route.length && s.fast) || Math.abs(gap) > 260 ? RUN : WALK;
        const speed = s.driving ? DRIVE : onFoot;
        s.facing = gap > 0 ? 1 : -1;
        s.vx = s.facing * speed;
        s.x += s.vx * dt;
        s.phase += (onFoot * dt) / 9;
        if (isText(s.ground) && !s.touring && Math.random() < SLIP_PER_S * dt) slip();
      } else {
        s.vx = 0;
      }
    };

    const inAir = (dt) => {
      const before = s.y;
      s.vy += GRAVITY * dt;
      if (s.chute) {
        // Floating down, swaying
        s.vy = Math.min(s.vy, GLIDE);
        s.vx = Math.sin(performance.now() / 650) * 48;
        s.tilt = Math.sin(performance.now() / 650 + 1.2) * 9;
      }
      s.x += s.vx * dt;
      s.y += s.vy * dt;
      // Bounce off the sides of the screen
      const wall = clamp(s.x, 30, window.innerWidth - 30);
      if (wall !== s.x && !s.offstage) {
        s.x = wall;
        s.vx *= -0.5;
      }
      if (s.vy <= 0) return;
      // A planned jump lands on its target; a fall lands on whatever is below
      const candidates = s.landing ? [s.landing] : perches();
      const under = candidates.find((g) => {
        if (g === "floor" || g === s.avoid || !usable(g)) return false;
        const [min, max] = span(g);
        const edge = top(g);
        return before <= edge + 30 && s.y >= edge && s.x > min - 24 && s.x < max + 24;
      });
      if (under) land(under);
      else if (s.y >= floorY()) land("floor");
    };

    const tick = (now) => {
      const dt = Math.min((now - last) / 1000, 0.05);
      last = now;
      frame = requestAnimationFrame(tick);
      if (!s.awake) return;

      if (!s.held) {
        if (s.ground) onGround(now, dt);
        if (!s.ground) inAir(dt);
      }

      // What is the pointer resting on? Mostly, it has something to say.
      if (pointer.seen && !pointer.rested && now - pointer.movedAt > REST_MS) {
        pointer.rested = true;
        const el = document.elementFromPoint(pointer.x, pointer.y);
        const thing = el && el.closest("[data-critter]");
        if (thing && !s.script.length && Math.random() < 0.75) remark(thing.dataset.critter);
      }

      // Eyes: at the pointer when it is around, otherwise where it is heading
      const watching = pointer.seen && now - pointer.movedAt < 6000;
      let lx = s.facing * 0.7;
      let ly = 0;
      if (s.asleep || now < s.staring) {
        lx = 0;
      } else if (watching && !s.script.length) {
        const dx = pointer.x - s.x;
        const dy = pointer.y - (s.y - 44);
        const d = Math.hypot(dx, dy) || 1;
        lx = dx / d;
        ly = dy / d;
      }
      s.lookX += (lx - s.lookX) * 0.2;
      s.lookY += (ly - s.lookY) * 0.2;
      pupils.forEach((p) => {
        p.style.transform = `translate(${s.lookX * 3}px, ${s.lookY * 3.4}px)`;
      });
      if (now > s.blinkAt) {
        root.classList.add("is-blinking");
        setTimeout(() => root.classList.remove("is-blinking"), 130);
        s.blinkAt = now + 1800 + Math.random() * 4200;
      }

      // Body: squash on landing, stretch in the air, lean into the walk
      s.squash *= Math.exp(-dt * 9);
      s.spin *= Math.exp(-dt * 4);
      s.tilt *= Math.exp(-dt * 6);
      s.flopNow += (s.flop * 86 - s.flopNow) * Math.min(1, dt * 14);
      const airborne = !s.ground;
      const stretch = airborne && !s.held ? Math.min(0.18, Math.abs(s.vy) / 4000) : 0;
      const sx = 1 + 0.22 * s.squash - stretch * 0.6;
      const sy = 1 - 0.26 * s.squash + stretch;
      const bob = !airborne && s.vx && !s.driving ? Math.abs(Math.sin(s.phase)) * 3 : 0;
      // On foot it leans into the walk; in the car it is pressed back into the seat
      const drift = s.driving ? -4 * Math.sign(s.vx) : clamp((s.vx / RUN) * 7, -24, 24);
      const lean = drift + s.tilt + s.spin + s.flopNow;
      s.sink += (s.sinkTo - s.sink) * Math.min(1, dt * 10);
      body.style.transform = `translateY(${s.sink - bob}px) rotate(${lean}deg) scale(${sx}, ${sy})`;
      limbs.style.transform = `scaleX(${s.facing})`; // feet point the way it walks
      car.style.transform = `scaleX(${s.facing})`;
      const swing = airborne ? 24 : s.vx ? Math.sin(s.phase) * 34 : 0;
      legs[0].style.transform = `rotate(${swing}deg)`;
      legs[1].style.transform = `rotate(${airborne ? -24 : -swing}deg)`;
      // Arms swing against the legs, and fly up in the air
      const reach = s.vx ? -Math.sin(s.phase) * 30 : Math.sin(now / 900) * 5;
      arms[0].style.transform = `rotate(${airborne || s.flop ? 120 : reach}deg)`;
      // The front arm (in walking direction) is the one that holds things up
      const front = s.holding ? -108 + Math.sin(now / 160) * (s.holding === "hand" ? 14 : 4) : reach;
      arms[1].style.transform = `rotate(${airborne || s.flop ? -120 : front}deg)`;
      prop.style.transform = `scaleX(${s.facing})`;
      root.classList.toggle("is-phone", s.holding === "phone");
      root.classList.toggle("is-cone", s.holding === "cone");
      root.classList.toggle("is-air", airborne);
      root.classList.toggle("is-flat", s.flop !== 0);
      root.classList.toggle("is-hiding", s.hiding);
      root.classList.toggle("is-gliding", s.chute);
      root.classList.toggle("is-driving", s.driving);
      const moving = s.driving && s.vx !== 0 && !!s.ground;
      root.classList.toggle("is-rolling", moving);
      if (moving) {
        // Wheelspin when it sets off, then a steady trail
        if (!rolling) for (let i = 0; i < 7; i++) dust(true);
        if (now - dustAt > 45) {
          dustAt = now;
          dust(false);
        }
      }
      rolling = moving;
      root.classList.toggle("is-gone", s.gone);

      root.style.transform = `translate3d(${s.x}px, ${s.y}px, 0)`;
      // The bubble keeps its place; it only rides along with a scrolling perch
      if (s.ground && s.ground === s.bubbleOn && now < s.talkUntil) s.bubbleY = s.y;
      const lift = window.innerWidth <= 820 ? 72 : 88;
      let bx = s.bubbleX;
      let by = s.bubbleY - lift;
      const bh = bubble.offsetHeight;
      if (by < bh + 8) {
        // No room above its head (it is near the top): put the bubble beside it
        const bw = bubble.offsetWidth;
        const side = s.bubbleX > window.innerWidth / 2 ? -1 : 1;
        bx = clamp(s.bubbleX + side * (bw / 2 + 46), bw / 2 + 8, Math.max(bw / 2 + 8, window.innerWidth - bw / 2 - 8));
        by = Math.max(s.bubbleY - 6, bh + 8);
      }
      bubble.style.setProperty("--x", `${bx}px`);
      bubble.style.setProperty("--y", `${by}px`);
    };

    // ---------- The visitor ----------

    const distanceTo = (x, y) => Math.hypot(x - s.x, y - (s.y - 40));

    const onMove = (e) => {
      const now = performance.now();
      if (grab) {
        if (!grab.moved && Math.hypot(e.clientX - grab.x, e.clientY - grab.y) > 7) {
          // Picked up
          grab.moved = true;
          stop();
          hush();
          s.touring = false;
          s.held = true;
          s.ground = null;
          s.landing = null;
          s.fell = false;
          say(pick(L().grab), 1600);
        }
        if (grab.moved) {
          const dt = Math.max((now - grab.at) / 1000, 0.008);
          const x = e.clientX;
          const y = e.clientY + 44; // held by the middle of the cap
          s.vx = s.vx * 0.5 + ((x - s.x) / dt) * 0.5;
          s.vy = s.vy * 0.5 + ((y - s.y) / dt) * 0.5;
          s.x = x;
          s.y = y;
          grab.at = now;
        }
      }
      // A fresh movement after a pause: is it interested this time?
      if (now - pointer.movedAt > 2500 && pointer.seen) s.curious = Math.random() < CURIOSITY;
      pointer.x = e.clientX;
      pointer.y = e.clientY;
      pointer.seen = true;
      pointer.movedAt = now;
      pointer.rested = false;
      activeAt = now;
      s.thinkAt = Math.min(s.thinkAt, now + 100);
      if (s.asleep && distanceTo(e.clientX, e.clientY) < NEAR) wake();
    };

    const onDown = (e) => {
      const now = performance.now();
      activeAt = now;
      if (!s.awake) return;
      const target = e.target.closest ? e.target : null;
      if (target && target.closest(".critter__bubble")) return; // its own buttons
      if (target && target.closest(".keyboard")) typedAt = now;
      if (s.asleep) {
        wake();
        return;
      }
      if (target && target.closest(".critter__hit")) {
        grab = { x: e.clientX, y: e.clientY, at: now, moved: false };
      } else if (distanceTo(e.clientX, e.clientY) < NEAR && !s.script.length) {
        hop(300); // startled
      }
    };

    const onUp = () => {
      if (!grab) return;
      const carried = grab.moved;
      grab = null;
      if (!carried) {
        if (s.touring) s.advance = true; // a tap on the guide means "go on"
        else poke();
        return;
      }
      s.held = false;
      s.vx = clamp(s.vx, -1500, 1500);
      s.vy = clamp(s.vy, -1700, 1500);
      s.jumpedAt = performance.now();
      s.habitAt = s.jumpedAt + 7000;
      if (Math.hypot(s.vx, s.vy) > 700) {
        s.spin = s.vx > 0 ? 360 : -360;
        s.thrown = true;
        say(pick(L().thrown), 1500);
      }
    };

    const onKey = (e) => {
      const now = performance.now();
      if (s.asleep) {
        if (now - typedAt < 600) wake(); // the keyboard typing by itself doesn't wake it
        return;
      }
      if (!s.script.length && distanceTo(e.detail.x, e.detail.y) < 520) hop(210);
      // Did the visitor just type a word it knows?
      if (now - typedAt > 600) return;
      activeAt = now;
      setTimeout(() => {
        const screen = document.querySelector(".display__typed span");
        const text = screen ? screen.textContent.trim() : "";
        const word = Object.keys(L().typed).find((w) => text.endsWith(w));
        if (word) {
          say(L().typed[word]);
          mood("is-happy", 1400);
        }
      }, 40);
    };

    // Typing in the form: it bounces along
    const onKeyDown = (e) => {
      const now = performance.now();
      if (e.key === "Escape" && s.touring) {
        endTour(L().tour.stopped);
        return;
      }
      if (!e.target.closest || !e.target.closest(".contact")) return;
      activeAt = now;
      if (s.asleep) wake();
      else if (now - keyHopAt > 170 && e.key.length === 1 && !s.script.length) {
        keyHopAt = now;
        hop(150);
      }
    };

    // A field got focus: go and stand by it, and say something about it
    const onFocus = (e) => {
      if (!e.target.matches || !e.target.matches("input, textarea")) return;
      const now = performance.now();
      const r = e.target.getBoundingClientRect();
      pointer.x = r.left + r.width / 2;
      pointer.y = r.top + r.height / 2;
      pointer.seen = true;
      pointer.movedAt = now;
      pointer.rested = true;
      s.curious = true;
      activeAt = now;
      s.thinkAt = Math.min(s.thinkAt, now + 100);
      if (s.asleep) wake();
      else if (e.target.dataset.critter) {
        if (!s.offstage) stop();
        remark(e.target.dataset.critter, { force: true });
      }
    };

    const onSent = () => {
      activeAt = performance.now();
      if (!s.awake) return;
      if (s.asleep) wake();
      stop();
      say(L().sent, 3200);
      act("party");
    };

    const onScroll = () => {
      const now = performance.now();
      const delta = window.scrollY - lastScroll;
      lastScroll = window.scrollY;
      activeAt = now;
      s.tilt = clamp(s.tilt + delta * 0.25, -16, 16);
      if (s.asleep && Math.abs(delta) > 30) wake();
      if (Math.abs(delta) > 140 && now - fastAt > 15000 && s.awake && !s.asleep && !s.touring) {
        fastAt = now;
        say(L().fast, 1800);
      }
    };

    const chat = (text, ms) => {
      if (s.awake && !s.asleep && !s.held) say(text, ms);
    };
    const onVisibility = () => {
      if (document.hidden) hiddenAt = performance.now();
      else if (hiddenAt && performance.now() - hiddenAt > 4000) {
        last = performance.now();
        chat(pick(L().away), 3000);
      }
    };
    const onCopy = () => chat(L().copy, 2400);
    const onMenu = () => chat(L().menu, 3000);
    const onResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        const width = window.innerWidth;
        if (Math.abs(width - lastWidth) > 80) chat(L().resize[width < lastWidth ? 0 : 1], 2200);
        lastWidth = width;
      }, 500);
    };

    // A slower look around: which section is this, which project is showing,
    // has the visitor reached the end
    const lookAround = setInterval(() => {
      if (!s.awake || s.asleep || s.offstage || s.gone || s.hiding || s.touring) return;
      const now = performance.now();
      const { innerHeight: h } = window;

      const shown = document.querySelector(".slide.is-active");
      const key = shown ? shown.dataset.critter : null;
      if (key !== slide) {
        const first = slide === null;
        slide = key;
        const r = shown ? shown.getBoundingClientRect() : null;
        if (r && !first && r.top < h * 0.75 && r.bottom > h * 0.25 && Math.random() < 0.6) {
          remark(key, { again: 90000 });
        }
      }

      if (now - remarkedAt < REMARK_GAP_MS) return;
      const atEnd = h + window.scrollY >= document.documentElement.scrollHeight - 40;
      if (atEnd && !said.has("bottom")) {
        said.add("bottom");
        say(L().bottom, 3200);
        return;
      }
      Object.keys(L().sections).some((id) => {
        const el = document.getElementById(id);
        if (!el || said.has(id)) return false;
        const r = el.getBoundingClientRect();
        if (r.top >= h / 2 || r.bottom <= h / 2) return false;
        said.add(id);
        say(L().sections[id], 3200);
        return true;
      });
    }, 700);

    const spawn = setTimeout(() => {
      const start = document.querySelector(".display");
      s.ground = start && usable(start) ? start : "floor";
      const [min, max] = span(s.ground);
      s.x = min + (max - min) * 0.82;
      s.y = top(s.ground);
      s.goal = s.x;
      s.awake = true;
      const now = performance.now();
      s.blinkAt = now + 1500;
      s.thinkAt = now + 2600;
      s.habitAt = now + 7000;
      s.awayAt = now;
      activeAt = now;
      root.classList.add("is-in");

      // It remembers who has been here before
      let visits = 0;
      try {
        visits = Number(localStorage.getItem(VISITS_KEY)) || 0;
        localStorage.setItem(VISITS_KEY, String(visits + 1));
      } catch (error) {
        // storage unavailable: everyone is a first-time visitor
      }
      say(visits ? pick(L().returning) : L().hello, 3200);
      mood("is-waving", 2400);

      // Once it has said hello, it offers to show a new visitor around
      let asked = false;
      try {
        asked = !!localStorage.getItem(TOUR_KEY);
      } catch (error) {
        // storage unavailable: offer anyway
      }
      if (!asked) {
        s.habitAt = now + 20000;
        s.script = [wait(4200), run(offerTour)];
      }
    }, SPAWN_MS);

    const night = setTimeout(() => {
      if (new Date().getHours() < 6) chat(L().night, 3600);
    }, 14000);
    const long = setTimeout(() => chat(L().long, 4200), 120000);

    frame = requestAnimationFrame(tick);
    const listeners = [
      [window, "pointermove", onMove, { passive: true }],
      [window, "pointerdown", onDown, { passive: true }],
      [window, "pointerup", onUp, { passive: true }],
      [window, "pointercancel", onUp, { passive: true }],
      [window, "scroll", onScroll, { passive: true }],
      [window, "resize", onResize],
      [window, "keydown", onKeyDown],
      [window, "focusin", onFocus],
      [window, KEY_EVENT, onKey],
      [window, SENT_EVENT, onSent],
      [document, "visibilitychange", onVisibility],
      [document, "copy", onCopy],
      [document, "contextmenu", onMenu],
      [bubble, "click", onChoice],
    ];
    listeners.forEach(([target, type, fn, options]) => target.addEventListener(type, fn, options));
    return () => {
      cancelAnimationFrame(frame);
      [spawn, night, long, talkTimer, moodTimer, resizeTimer].forEach(clearTimeout);
      clearInterval(lookAround);
      listeners.forEach(([target, type, fn]) => target.removeEventListener(type, fn));
      endShow();
      layer.querySelectorAll(".critter__dust, .critter__photo, .critter__hud, .critter__ping, .critter__mark").forEach((el) => el.remove());
      root.classList.remove("is-in", "is-asleep", "is-blinking", "is-air", "is-happy", "is-waving", "is-flat", "is-hiding", "is-gone", "is-gliding", "is-driving", "is-rolling", "is-phone", "is-cone");
      bubble.classList.remove("is-on");
    };
  }, [reducedMotion]);

  // It notices when the lights change
  const firstTheme = useRef(true);
  useEffect(() => {
    if (firstTheme.current) {
      firstTheme.current = false;
      return;
    }
    sayRef.current(theme === "dark" ? lines.current.dark : lines.current.light);
  }, [theme]);

  if (reducedMotion) return null;

  return (
    <div className="critter-layer" aria-hidden="true">
      <span className="critter__bubble">
        <span className="critter__say" />
        <span className="critter__actions">
          <button type="button" className="critter__act critter__act--main" tabIndex={-1} />
          <button type="button" className="critter__act" tabIndex={-1} />
        </span>
      </span>
      <div className="critter" ref={rootRef}>
        <div className="critter__pop">
          <svg className="critter__chute" viewBox="0 0 120 112" width="120" height="112">
            <path className="critter__strings" d="M8 50 L44 110 M60 52 L60 110 M112 50 L76 110" />
            <path className="critter__canopy" d="M6 52 Q60 -40 114 52 Q96 40 78 52 Q60 40 42 52 Q24 40 6 52 Z" />
            <path className="critter__gore" d="M42 52 Q46 12 60 6 Q74 12 78 52 Q60 40 42 52 Z" />
          </svg>
          <span className="critter__zzz">z z z</span>
          <div className="critter__body">
            <svg className="critter__art" viewBox="0 0 72 78" width="72" height="78">
              <defs>
                <linearGradient id="critter-top" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0" className="critter__top-light" />
                  <stop offset="1" className="critter__top-dark" />
                </linearGradient>
              </defs>
              <ellipse className="critter__shadow" cx="36" cy="75.5" rx="19" ry="2.5" />
              <g className="critter__limbs">
                <g className="critter__leg">
                  <rect x="23.5" y="56" width="5" height="16" rx="2.5" />
                  <ellipse cx="27.5" cy="73" rx="6" ry="3.6" />
                </g>
                <g className="critter__leg">
                  <rect x="43.5" y="56" width="5" height="16" rx="2.5" />
                  <ellipse cx="47.5" cy="73" rx="6" ry="3.6" />
                </g>
                <rect className="critter__arm critter__arm--left" x="2.5" y="35" width="5.5" height="15" rx="2.75" />
                <rect className="critter__arm critter__arm--right" x="64" y="35" width="5.5" height="15" rx="2.75" />
              </g>
              <g className="critter__hit">
                {/* A keycap seen from the front: dark skirt, lighter dished top */}
                <rect className="critter__skirt" x="5" y="9" width="62" height="52" rx="15" />
                <rect x="9" y="6" width="54" height="46" rx="13" fill="url(#critter-top)" />
                <path className="critter__shine" d="M19 10.5 h26" />
                <g className="critter__face">
                  <ellipse className="critter__cheek" cx="16.5" cy="38" rx="4.5" ry="2.8" />
                  <ellipse className="critter__cheek" cx="55.5" cy="38" rx="4.5" ry="2.8" />
                  <g className="critter__eyes">
                    <g className="critter__eye">
                      <ellipse cx="25" cy="27.5" rx="8" ry="9" fill="#fff" />
                      <g className="critter__pupil">
                        <circle cx="25" cy="28" r="4.6" fill="#1e1d1b" />
                        <circle cx="23.4" cy="26.2" r="1.6" fill="#fff" />
                      </g>
                    </g>
                    <g className="critter__eye">
                      <ellipse cx="47" cy="27.5" rx="8" ry="9" fill="#fff" />
                      <g className="critter__pupil">
                        <circle cx="47" cy="28" r="4.6" fill="#1e1d1b" />
                        <circle cx="45.4" cy="26.2" r="1.6" fill="#fff" />
                      </g>
                    </g>
                  </g>
                  {/* Eyes while asleep (‿ ‿) and while delighted (^ ^) */}
                  <path className="critter__line critter__shut" d="M19 28 q6 5 12 0 M41 28 q6 5 12 0" />
                  <path className="critter__line critter__glad" d="M19 30 q6 -7 12 0 M41 30 q6 -7 12 0" />
                  <path className="critter__line critter__smile" d="M31 40 q5 5 10 0" />
                  <ellipse className="critter__gasp" cx="36" cy="42" rx="3.4" ry="4" />
                </g>
              </g>
            </svg>
          </div>
          {/* What it holds up in its front hand: a phone, or an ice cream */}
          <svg className="critter__prop" viewBox="-36 -78 72 78" width="72" height="78">
            <g className="critter__phone">
              <rect x="27" y="-62" width="13" height="23" rx="3" fill="#1e1d1b" stroke="#f4f1e8" strokeWidth="1.2" />
              <rect x="29" y="-59.5" width="9" height="16" rx="1.2" fill="#8fd0ee" />
              <circle cx="33.5" cy="-41.6" r="1" fill="#f4f1e8" />
            </g>
            <g className="critter__cone">
              <path d="M27.5 -52 L33.5 -34 L39.5 -52 Z" fill="#d9a35c" stroke="#8a5a26" strokeWidth="1.2" strokeLinejoin="round" />
              <path d="M30 -48 L37 -48 M31.5 -43.5 L35.5 -43.5" stroke="#8a5a26" strokeWidth="1" />
              <circle cx="33.5" cy="-56.5" r="6.5" fill="#fbe9c9" stroke="#1e1d1b" strokeWidth="1.2" />
              <circle cx="33.5" cy="-65" r="5.2" fill="#f29bb0" stroke="#1e1d1b" strokeWidth="1.2" />
              <circle cx="31.6" cy="-66.6" r="1.3" fill="#fff" opacity="0.8" />
            </g>
          </svg>
          {/* Its car, drawn facing right: a chunky open-top off-roader. In front
              of the body, so the critter sits inside it. */}
          <svg className="critter__car" viewBox="0 0 150 84" width="150" height="84">
            <defs>
              <linearGradient id="critter-car-body" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor="#fdfbf5" />
                <stop offset="0.55" stopColor="#efe9dc" />
                <stop offset="1" stopColor="#d3ccbb" />
              </linearGradient>
              <linearGradient id="critter-car-glass" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0" stopColor="#d9f1fb" />
                <stop offset="1" stopColor="#7fb9d6" />
              </linearGradient>
              <radialGradient id="critter-car-rim" cx="0.4" cy="0.35" r="0.8">
                <stop offset="0" stopColor="#f3f0e8" />
                <stop offset="1" stopColor="#9d988c" />
              </radialGradient>
            </defs>
            <ellipse className="critter__car-shadow" cx="75" cy="82" rx="64" ry="3" />
            {/* Behind the body: antenna, spare wheel, roll bar, windshield, mirror */}
            <path className="critter__car-antenna" d="M17 31 L13 8" />
            <rect className="critter__car-dark" x="0" y="30" width="11" height="31" rx="5" />
            <path className="critter__car-bar" d="M29 32 V14 Q29 8 34.5 8 Q40 8 40 14 V32" />
            <path
              d="M99 31 L101 7 Q101.3 4 104 4 L106 4 Q108.5 4 108.5 7 L108.5 31 Z"
              fill="url(#critter-car-glass)"
              stroke="#1e1d1b"
              strokeWidth="2.2"
              strokeLinejoin="round"
            />
            <path className="critter__car-glint" d="M103.5 9 L102.5 24" />
            <path className="critter__car-dark" d="M90 21 h7 q2 0 2 2 v4 q0 2 -2 2 h-7 q-2 0 -2 -2 v-4 q0 -2 2 -2 Z" />
            <path
              d="M10 37 Q10 30 17 30 L104 30 L108.5 34 L133 34 Q140 34 140 41 L140 60 Q140 66 134 66 L16 66 Q10 66 10 60 Z"
              fill="url(#critter-car-body)"
              stroke="#1e1d1b"
              strokeWidth="2.2"
              strokeLinejoin="round"
            />
            <path className="critter__car-glint" d="M18 33.2 H101 M112 37.2 H132" />
            <path className="critter__car-strip" d="M11.5 49 H139" />
            <path className="critter__car-door" d="M58 35 H96 Q98 35 98 37 V64 H56 V37 Q56 35 58 35 Z M56 39 h-3 M56 60 h-3" />
            <rect className="critter__car-handle" x="85" y="40" width="9" height="3.4" rx="1.7" />
            <rect className="critter__car-tail" x="10.5" y="37" width="3.6" height="9" rx="1.4" />
            <rect className="critter__car-blinker" x="123" y="29.5" width="10" height="4.6" rx="2" />
            <circle className="critter__car-light" cx="139" cy="43" r="4.6" />
            <circle className="critter__car-light-core" cx="139" cy="43" r="2" />
            <rect className="critter__car-dark" x="3" y="59" width="14" height="8" rx="3" />
            <rect className="critter__car-dark" x="133" y="59" width="15" height="8" rx="3" />
            <path className="critter__car-chrome" d="M57 70 H97" />
            {/* Square wheel arches */}
            <path className="critter__car-dark" d="M17 67 V52 Q17 44 25 44 H47 Q55 44 55 52 V67 Z" />
            <path className="critter__car-dark" d="M93 67 V52 Q93 44 101 44 H123 Q131 44 131 52 V67 Z" />
            <g className="critter__wheel">
              <circle className="critter__tire" cx="36" cy="65" r="17" />
              <circle className="critter__tread" cx="36" cy="65" r="15" />
              <circle cx="36" cy="65" r="9.5" fill="url(#critter-car-rim)" stroke="#55514a" strokeWidth="1.2" />
              <path
                className="critter__spokes"
                d="M36 56.5 V73.5 M27.9 62.4 L44.1 67.6 M44.1 62.4 L27.9 67.6 M31 58.1 L41 71.9 M41 58.1 L31 71.9"
              />
              <circle className="critter__hub" cx="36" cy="65" r="3.2" />
            </g>
            <g className="critter__wheel">
              <circle className="critter__tire" cx="112" cy="65" r="17" />
              <circle className="critter__tread" cx="112" cy="65" r="15" />
              <circle cx="112" cy="65" r="9.5" fill="url(#critter-car-rim)" stroke="#55514a" strokeWidth="1.2" />
              <path
                className="critter__spokes"
                d="M112 56.5 V73.5 M103.9 62.4 L120.1 67.6 M120.1 62.4 L103.9 67.6 M107 58.1 L117 71.9 M117 58.1 L107 71.9"
              />
              <circle className="critter__hub" cx="112" cy="65" r="3.2" />
            </g>
          </svg>
        </div>
      </div>
    </div>
  );
};

export default Critter;
